import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { SERVICE_TYPES } from './maintenanceShared';
import { AvailabilityHint } from './AvailabilityHint';

const today = () => new Date().toLocaleDateString('sv-SE');

// Generic form dialog for create / request / schedule / complete / reject
export const MaintenanceFormDialog = ({ open, onOpenChange, title, el, fields, endpoint, buildBody, submitLabel, danger, onDone, vehicles, workshops = [], initial = {}, excludeId }) => {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const val = (k, d = '') => form[k] ?? initial[k] ?? d;

    const submit = async () => {
        setSaving(true);
        try {
            await api.post(endpoint, buildBody({ ...initial, ...form, scheduled_date: val('scheduled_date', today()), completed_date: val('completed_date', today()), proposed_date: val('proposed_date', today()) }));
            toast.success(el ? 'Αποθηκεύτηκε' : 'Saved');
            setForm({});
            onOpenChange(false);
            onDone();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) setForm({}); onOpenChange(o); }}>
            <DialogContent className="bg-white" data-testid="maintenance-form-dialog">
                <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    {fields.includes('vehicle') && (
                        <div className="space-y-1">
                            <Label>{el ? 'Όχημα' : 'Vehicle'}</Label>
                            <Select value={val('vehicle')} onValueChange={(v) => set('vehicle', v)}>
                                <SelectTrigger data-testid="maint-vehicle-select"><SelectValue placeholder={el ? 'Επιλέξτε όχημα' : 'Select vehicle'} /></SelectTrigger>
                                <SelectContent className="bg-white">
                                    {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.license_plate} - {v.make} {v.model} ({v.client_company_name})</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    {fields.includes('service_type') && (
                        <div className="space-y-1">
                            <Label>{el ? 'Είδος συντήρησης' : 'Service type'}</Label>
                            <Select value={val('service_type')} onValueChange={(v) => set('service_type', v)}>
                                <SelectTrigger data-testid="maint-type-select"><SelectValue placeholder={el ? 'Επιλέξτε' : 'Select'} /></SelectTrigger>
                                <SelectContent className="bg-white">
                                    {SERVICE_TYPES.map((t) => <SelectItem key={t.el} value={el ? t.el : t.en}>{el ? t.el : t.en}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    {fields.includes('description') && (
                        <div className="space-y-1"><Label>{el ? 'Περιγραφή' : 'Description'}</Label><Textarea value={val('description')} onChange={(e) => set('description', e.target.value)} data-testid="maint-description" /></div>
                    )}
                    {fields.includes('scheduled_date') && (
                        <div className="space-y-1"><Label>{el ? 'Ημερομηνία συντήρησης' : 'Service date'}</Label><Input type="date" min={today()} value={val('scheduled_date', today())} onChange={(e) => set('scheduled_date', e.target.value)} data-testid="maint-scheduled-date" /></div>
                    )}
                    {fields.includes('workshop') && (
                        <div className="space-y-1">
                            <Label>{el ? 'Συνεργείο (προαιρετικό)' : 'Workshop (optional)'}</Label>
                            <Select value={val('workshop')} onValueChange={(v) => set('workshop', v)}>
                                <SelectTrigger data-testid="maint-workshop-select"><SelectValue placeholder={workshops.length ? (el ? 'Επιλέξτε συνεργείο' : 'Select workshop') : (el ? 'Δεν υπάρχουν συνεργεία' : 'No workshops')} /></SelectTrigger>
                                <SelectContent className="bg-white">
                                    <SelectItem value="none">{el ? 'Χωρίς συνεργείο' : 'No workshop'}</SelectItem>
                                    {workshops.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}{w.city ? ` - ${w.city}` : ''}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    {fields.includes('workshop') && fields.includes('scheduled_date') && (
                        <AvailabilityHint workshopId={val('workshop')} date={val('scheduled_date', today())} excludeId={excludeId} el={el} />
                    )}
                    {fields.includes('completed_date') && (
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1"><Label>{el ? 'Ημερομηνία ολοκλήρωσης' : 'Completed on'}</Label><Input type="date" value={val('completed_date', today())} onChange={(e) => set('completed_date', e.target.value)} data-testid="maint-completed-date" /></div>
                            <div className="space-y-1"><Label>{el ? 'Κόστος (€)' : 'Cost (€)'}</Label><Input type="number" min="0" value={val('cost')} onChange={(e) => set('cost', e.target.value)} data-testid="maint-cost" /></div>
                        </div>
                    )}
                    {fields.includes('mileage') && (
                        <div className="space-y-1"><Label>{el ? 'Χιλιόμετρα οχήματος' : 'Vehicle mileage (km)'}</Label><Input type="number" min="0" value={val('mileage')} onChange={(e) => set('mileage', e.target.value)} data-testid="maint-mileage" /></div>
                    )}
                    {fields.includes('proposed_date') && (
                        <div className="space-y-1"><Label>{el ? 'Προτεινόμενη ημερομηνία' : 'Proposed date'}</Label><Input type="date" min={today()} value={val('proposed_date', today())} onChange={(e) => set('proposed_date', e.target.value)} data-testid="maint-proposed-date" /></div>
                    )}
                    {fields.includes('notes') && (
                        <div className="space-y-1"><Label>{el ? 'Σημειώσεις' : 'Notes'}</Label><Textarea value={val('notes')} onChange={(e) => set('notes', e.target.value)} data-testid="maint-notes" /></div>
                    )}
                    {fields.includes('comment') && (
                        <div className="space-y-1"><Label>{el ? 'Σχόλιο προς το συνεργείο' : 'Comment for the workshop'}</Label><Textarea value={val('comment')} onChange={(e) => set('comment', e.target.value)} data-testid="maint-comment" /></div>
                    )}
                    {fields.includes('reason') && (
                        <div className="space-y-1"><Label>{el ? 'Λόγος απόρριψης' : 'Rejection reason'}</Label><Textarea value={val('reason')} onChange={(e) => set('reason', e.target.value)} data-testid="maint-reason" /></div>
                    )}
                    <Button onClick={submit} disabled={saving} className={`w-full text-white rounded-sm ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-[#1B7EB5] hover:bg-[#58A6B4]'}`} data-testid="maint-submit">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : submitLabel}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};
